import { useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { faqs, faqCategories } from "@/data/faqs";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";

export default function FAQs() {
    const [active, setActive] = useState("All");
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return faqs.filter((f) => {
            const matchesCat = active === "All" || f.category === active;
            const matchesQ =
                !q ||
                f.question.toLowerCase().includes(q) ||
                f.answer.toLowerCase().includes(q) ||
                f.category.toLowerCase().includes(q);
            return matchesCat && matchesQ;
        });
    }, [active, query]);

    return (
        <div data-testid="page-faqs">
            {/* Hero */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-24">
                    <div className="max-w-3xl animate-fade-up">
                        <p className="overline text-tca-orange mb-4">FAQs · Established 2015 · CA(SA) · RA</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            100 answers
                            <span className="block text-tca-orange">to the questions our clients actually ask.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            South African tax, IFRS, Companies Act, VAT, PAYE, UIF, trusts and estate duty — the principles every owner should understand. Each answer ends with the same advice: book a consultation before acting on your specific facts.
                        </p>
                    </div>
                </div>
            </section>

            {/* Search + filter */}
            <section className="bg-white sticky top-[110px] z-30 border-b border-tca-line">
                <div className="container-tca py-5">
                    <div className="flex flex-col gap-3">
                        <div className="relative w-full max-w-2xl" data-testid="faq-search-wrap">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tca-orange" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search 100 questions..."
                                data-testid="faq-search-input"
                                className="w-full pl-10 pr-9 py-3 border border-tca-orange bg-orange-50 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft placeholder:text-tca-orange/50"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-tca-mute hover:text-tca-ink"
                                    data-testid="faq-search-clear"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="faq-categories">
                            {faqCategories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setActive(c)}
                                    data-testid={`faq-cat-${c.toLowerCase().replace(/\s|&/g, "-")}`}
                                    className={`text-xs font-bold uppercase tracking-[0.14em] px-3.5 py-2 border transition-colors ${
                                        active === c
                                            ? "bg-tca-ink text-white border-tca-ink"
                                            : "bg-white text-tca-ink border-tca-line hover:border-tca-orange hover:text-tca-orange"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Accordion list */}
            <section className="bg-white">
                <div className="container-tca py-12 md:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <aside className="lg:col-span-3 lg:sticky lg:top-[200px] lg:self-start">
                            <div className="border-l-4 border-tca-orange pl-5">
                                <p className="overline mb-3">Showing</p>
                                <p className="font-display font-extrabold text-5xl text-tca-ink">{filtered.length}</p>
                                <p className="text-sm text-tca-mute mt-1">{filtered.length === 1 ? "question" : "questions"}</p>
                            </div>
                            <div className="hidden lg:block mt-8 p-8 bg-tca-fog border border-tca-line">
                                <p className="font-display font-bold text-tca-ink mb-2">Need a tailored answer?</p>
                                <p className="text-sm text-tca-mute mb-5">
                                    Every answer here is general. Your facts will change the advice — book a meeting.
                                </p>
                                <CTAButtons testIdPrefix="faqs-side-cta" inquiryLabel="Send Inquiry" scheduleLabel="Book a Meeting" className="sm:!flex-col" />
                            </div>
                        </aside>

                        <div className="lg:col-span-9">
                            {filtered.length === 0 ? (
                                <div className="border border-tca-line p-10 text-center" data-testid="faq-empty">
                                    <p className="font-display font-bold text-tca-ink text-xl mb-2">Nothing matches that yet.</p>
                                    <p className="text-tca-mute text-sm mb-5">Try another keyword or clear the filter.</p>
                                    <button
                                        onClick={() => {
                                            setActive("All");
                                            setQuery("");
                                        }}
                                        data-testid="faq-empty-reset"
                                        className="btn-secondary inline-flex"
                                    >
                                        Reset filters
                                    </button>
                                </div>
                            ) : (
                                <Accordion type="single" collapsible className="border-t border-tca-line" data-testid="faq-accordion">
                                    {filtered.map((f, idx) => (
                                        <AccordionItem
                                            key={`${f.category}-${idx}-${f.question.slice(0, 30)}`}
                                            value={`faq-${idx}`}
                                            data-testid={`faq-item-${idx}`}
                                            className="border-b border-tca-line"
                                        >
                                            <AccordionTrigger className="py-6 text-left hover:no-underline group [&[data-state=open]>svg]:rotate-180">
                                                <div className="flex-1 flex items-start gap-5 pr-6">
                                                    <span className="font-display font-bold text-xs uppercase tracking-[0.14em] text-tca-orange pt-1 shrink-0 w-32 hidden md:block">
                                                        {f.category}
                                                    </span>
                                                    <span className="font-display font-bold text-base md:text-lg text-tca-ink group-hover:text-tca-orange transition-colors">
                                                        {f.question}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="pb-6">
                                                <div className="md:pl-[120px] text-tca-mute leading-relaxed">
                                                    {f.answer}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* Bottom CTA */}
            <section className="bg-tca-ink text-white">
                <div className="container-tca py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white leading-[1.05]">Still no answer that fits your case?</h2>
                        <p className="mt-4 text-white/70 max-w-xl">A single 30-minute meeting with TC Auditors usually solves it. Don't act on internet advice — speak to a CA(SA).</p>
                    </div>
                    <CTAButtons testIdPrefix="faqs-bottom-cta" />
                </div>
            </section>
        </div>
    );
}
